import type { Position } from "../types/grid.js";
import type { GridMap } from "./GridMap.js";

/** A* pathfinder for enemy navigation on the logical grid. */

interface Node {
  pos: Position;
  g: number; // cost from start
  h: number; // heuristic to goal
  f: number; // g + h
  parent: Node | null;
}

function heuristic(a: Position, b: Position): number {
  // Manhattan distance — appropriate for 4-directional grid movement
  return Math.abs(a.x - b.x) + Math.abs(a.z - b.z);
}

const NEIGHBORS: readonly Position[] = [
  { x: 0, z: -1 }, // north
  { x: 1, z: 0 },  // east
  { x: 0, z: 1 },  // south
  { x: -1, z: 0 }, // west
];

/**
 * Returns an array of positions from `start` to `goal` (exclusive of start),
 * or an empty array if no path exists.
 */
export function findPath(grid: GridMap, start: Position, goal: Position): Position[] {
  const key = (p: Position): string => `${p.x},${p.z}`;

  const open = new Map<string, Node>();
  const closed = new Set<string>();

  const startNode: Node = { pos: start, g: 0, h: heuristic(start, goal), f: 0, parent: null };
  startNode.f = startNode.g + startNode.h;
  open.set(key(start), startNode);

  while (open.size > 0) {
    // Find the open node with the lowest f score
    let current: Node | null = null;
    for (const node of open.values()) {
      if (!current || node.f < current.f) current = node;
    }
    if (!current) break;

    if (current.pos.x === goal.x && current.pos.z === goal.z) {
      // Reconstruct path
      const path: Position[] = [];
      let node: Node | null = current;
      while (node?.parent) {
        path.unshift(node.pos);
        node = node.parent;
      }
      return path;
    }

    open.delete(key(current.pos));
    closed.add(key(current.pos));

    for (const delta of NEIGHBORS) {
      const neighbor: Position = { x: current.pos.x + delta.x, z: current.pos.z + delta.z };
      const nKey = key(neighbor);
      if (closed.has(nKey)) continue;
      if (!grid.isPassable(neighbor)) continue;

      const g = current.g + 1;
      const existing = open.get(nKey);
      if (existing && g >= existing.g) continue;

      const h = heuristic(neighbor, goal);
      const node: Node = { pos: neighbor, g, h, f: g + h, parent: current };
      open.set(nKey, node);
    }
  }

  return []; // no path found
}
